import type { Capabilities } from '../api/meta';
import type { Permissions } from './types';
import { hasRole, ROLES } from './permissions';

/**
 * The second of the two gates.
 *
 * Permissions answer "may THIS USER do it"; capabilities answer "does THIS DEPLOYMENT have it
 * at all". Both have to pass before a control is drawn, and the reason is the air-gapped
 * build: there the AI surfaces must be ABSENT rather than present-and-failing, because a
 * button that errors is worse than a button that is not there.
 *
 * Unknown means PRESENT. A server that predates `/api/capabilities`, or a fetch that failed,
 * leaves every feature offered — exactly as the app behaved before the endpoint existed. The
 * server enforces the same rules independently with `requireCapability()`, so a permissive
 * default here costs a 503, not a hole.
 */
export function capAi(caps: Capabilities | null | undefined, name: keyof Capabilities['ai']): boolean {
  if (!caps || !caps.ai) return true;
  return caps.ai[name] !== false;
}

/**
 * The AI-admin gate as the app actually applies it: the role AND the capability.
 *
 * `permissions.canAiAdmin()` is the role half alone. Both exist because some call sites
 * genuinely want only the role — but every control that OPENS an AI feature wants this one,
 * and having them differ by a name that reads the same is how the offline build ends up with
 * a button that 503s.
 */
export const canAiAdminHere = (
  perm: Permissions | null | undefined,
  caps: Capabilities | null | undefined,
): boolean => hasRole(perm, ROLES.aiAdmin) && capAi(caps, 'docgen');

/**
 * Live verification — the agent that inspects a real system before documenting it.
 *
 * A capability of its own rather than part of doc generation, because an offline build ships
 * without the browser the agent drives. Same role, different question.
 */
export const canTranslateDocsHere = (
  perm: Permissions | null | undefined,
  caps: Capabilities | null | undefined,
): boolean => hasRole(perm, ROLES.aiAdmin) && capAi(caps, 'translate');

export const canLiveVerifyHere = (
  perm: Permissions | null | undefined,
  caps: Capabilities | null | undefined,
): boolean => hasRole(perm, ROLES.aiAdmin) && capAi(caps, 'liveVerify');
